import { useState, useEffect } from "react";
import { isNotEmpty } from "lcm-iot-commons";
import scriptjs from "scriptjs";
import { injectIntl } from "react-intl";

let realZxcvbn: $TSFixMe;
const BLACKLISTED_PASSWORDS = [
  "Endress",
  "Hauser",
  "Reinach",
  "Freiburg",
  "Proline",
  "Promass",
  "Micropilot",
  "Liquiline",
  "FieldCare",
  "IIoT",
  "Flow",
  "Netilion",
  "Endress+Hauser",
  "Fermentation",
  "Floodlight",
];

export function loadZxcvbn(): Promise<void> {
  // istanbul ignore next
  if (realZxcvbn) {
    return Promise.resolve();
  }
  // istanbul ignore next
  if (typeof process !== "undefined" && process?.env?.NODE_ENV === "test") {
    // eslint-disable-next-line global-require
    realZxcvbn = require("zxcvbn");
    return Promise.resolve();
  }

  // istanbul ignore next
  return new Promise((resolve, reject) => {
    scriptjs(
      "/app/id/javascript/zxcvbn.js",
      () => {
        realZxcvbn = (global.window as $TSFixMe).zxcvbn;
        resolve();
      },
      () => {
        /* eslint-disable no-console */
        console.error(`not found zxcvbn.js, please check the path`);
        /* eslint-enable no-console */
        reject();
      },
    );
  });
}

export function extractUserInputs(
  values: Record<string, string>,
  keysToIgnore: string[],
): string[] {
  return Object.keys(values)
    .filter((key) => keysToIgnore.indexOf(key) < 0)
    .map((key) => values[key]);
}

export type CheckPasswordResult = {
  suggestions: string[];
  error: string | null;
  success: string | null;
};
export function checkPassword(
  password: string,
  intl: $TSFixMe,
  userInputs: string[] = [],
): CheckPasswordResult {
  const blackListedPasswords = BLACKLISTED_PASSWORDS.concat(userInputs);
  const result = realZxcvbn(
    password || /* istanbul ignore next */ "",
    blackListedPasswords,
  );

  const passwordWarning = isNotEmpty(result.feedback.warning)
    ? intl.formatMessage({ id: result.feedback.warning })
    : null;
  const passwordSuggestions = result.feedback.suggestions.map(
    (message: string) => intl.formatMessage({ id: message }),
  );

  const checkResult: CheckPasswordResult = {
    suggestions: passwordSuggestions,
    error: null,
    success: null,
  };
  if (result.score > 3) {
    const good = intl.formatMessage({ id: "validation.password.good" });
    checkResult.success = passwordWarning
      ? /* istanbul ignore next */ `${good} ${passwordWarning}`
      : good;
  } else if (result.score > 2) {
    const weak = intl.formatMessage({ id: "validation.password.weak" });
    checkResult.error = passwordWarning
      ? /* istanbul ignore next */ `${weak} ${passwordWarning}`
      : weak;
  } else {
    // result.score 2, 1 or 0
    const invalid = intl.formatMessage({ id: "validation.password.invalid" });
    checkResult.error = passwordWarning
      ? `${invalid} ${passwordWarning}`
      : invalid;
  }
  return checkResult;
}

/* istanbul ignore next */
const withZxcvbn = (ChildComponent: $TSFixMe) => {
  const WrappedComponent = injectIntl((props) => {
    const createZxcvbn = () => {
      if (realZxcvbn) {
        return {
          checkPassword: (password: $TSFixMe, userInputs: $TSFixMe) =>
            checkPassword(password, props.intl, userInputs),
          extractUserInputs,
        };
      }

      return undefined;
    };

    const [zxcvbn, setZxcvbn] = useState(createZxcvbn());

    useEffect(() => {
      const load = async () => {
        await loadZxcvbn();
        setZxcvbn(createZxcvbn());
      };

      if (!realZxcvbn) {
        load();
      }
    }, []);

    return zxcvbn ? <ChildComponent {...props} zxcvbn={zxcvbn} /> : null;
  });

  WrappedComponent.displayName =
    ChildComponent.displayName || ChildComponent.name;
  return WrappedComponent;
};

type zxcvbnShape = {
  checkPassword: (
    password: string,
    userInputs: string[],
  ) => CheckPasswordResult;
  extractUserInputs: (
    values: Record<string, string>,
    keysToIgnore: string[],
  ) => string[];
};

// @ts-expect-error TS(1205) FIXME: Re-exporting a type when the '--isolatedModules' f... Remove this comment to see the full error message
export { zxcvbnShape };

export default withZxcvbn;
